import { useEffect } from 'react';
import Media from 'react-media';
import track, { useTracking } from 'react-tracking';
import { Column, Row, useTheme, Heading, Box } from '@simplywallst/ui-core';
import { SWS_HOST } from '@/constants';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import useTitleGenerator, {
  usIndices,
} from '@/pages/Market/hooks/useTitleGenerator';
import useReduxStateMarket, {
  MarketPageType,
} from '@/pages/Market/hooks/useReduxStateMarkets';
import GlobalStyle from '@/styled/styled.default';
import DocumentHeader from '@components/DocumentHeader';
import Footer from '@components/Footer';
import { OneColumn } from '@components/Layout';
import CompanyFeeds from '../CompanyFeeds';
import Header from '../Header';
import MarketPerformance from '../MarketPerformance';
import MarketTable from '../MarketTable';
import Sectors from '../Sectors';
import MarketFAQ from '../MarketFAQ';
import Summary from '../Summary';
import Widget from '../Widget';
import useIndustryGetter from '../../hooks/useIndustryGetter';
import { useIndustryMetrics } from '@/hooks/useIndustryMetrics';
import ScrollTracking from '@/pages/Market/components/ScrollTracking';
import {
  MARKET_GAINER_ANCHOR,
  MARKET_TRENDS_ANCHOR,
  MARKET_VALUATION_ANCHOR,
} from '@/constants/anchors';
import { Container, SidebarWrapper, FixedSidebar } from './styled';
import RecentArticles from '@/components/RecentArticles';
import { useFlag } from '@/features/unleash';

const Market = () => {
  const theme = useTheme();
  const featureFlag = useFlag({ name: 'mono-dashboard-portfolio-widget' });
  const isNewPortfolioSectionEnabled =
    featureFlag.flagsReady && featureFlag.enabled;

  const { market, sector, secondary, tertiary, pathname, marketPageType } =
    useReduxStateMarket();
  const [{ id }, enabled] = useIndustryGetter();
  const { latestAnalysis } = useIndustryMetrics(
    market,
    id,
    marketPageType === MarketPageType.MARKET ? true : enabled
  );
  const { trackEvent } = useTracking();
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    } else {
      const hash = window.location.hash;
      const cleanHash = hash.split('?')[0];
      window.setTimeout(() => {
        const targetEl = document.querySelector(cleanHash);
        targetEl?.scrollIntoView();
      }, 0);
    }
  }, [market]);

  useEffect(() => {
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'landed',
      market,
      industryId: id,
    });
  }, [market, sector, secondary, tertiary]);

  const pageTitle = useTitleGenerator(
    {
      marketTemplate: '[Market] Market Analysis & Valuation',
      sectorTemplate: '[Market] [Sector] Sector Analysis',
      industryTemplate: '[Market] [Industry] Industry Analysis',
    },
    true
  );
  const pageTitleSuffix = useTitleGenerator({
    marketTemplate: market === 'us' ? `- ${usIndices}` : '- Updated Today',
    sectorTemplate: '',
    industryTemplate: '',
  });
  const sectorTitle = useTitleGenerator({
    marketTemplate: 'Sector Trends',
    sectorTemplate: 'Industry Trends',
    industryTemplate: 'Industry Trends',
    tertiaryTemplate: 'Industry Comparison',
  });
  const sectorSubtitle = useTitleGenerator({
    marketTemplate:
      'Which sectors have driven the changes within the [Market] Market?',
    sectorTemplate:
      'Which industries have driven the changes within the [Market] [Sector] sector?',
    industryTemplate:
      'Which industries have driven the changes within the [Market] [ParentIndustry] industry?',
    tertiaryTemplate:
      'How does [Market] [Industry] compare with similar industries?',
  });
  const marketPerformance = useTitleGenerator({
    marketTemplate: 'Market Valuation and Performance',
    sectorTemplate: 'Sector Valuation and Performance',
    industryTemplate: 'Industry Valuation and Performance',
  });
  const marketPerformanceSubtitle = useTitleGenerator({
    marketTemplate:
      'Has the [Market] Market valuation changed over the past few years?',
    sectorTemplate:
      'Has the [Market] [Sector] Sector valuation changed over the past few years?',
    industryTemplate:
      'Has the [Market] [Industry] Industry valuation changed over the past few years?',
  });

  const metaDescription = useTitleGenerator({
    marketTemplate:
      'Daily update of the [Market] market. From market news, valuation, and performance to stock trends, gainers, and losers',
    sectorTemplate:
      'Discover the state of the [Market] [Sector] Sector. From valuation and performance to stock trends, gainers, and losers.',
    industryTemplate:
      'Discover the state of the [Market] [Industry] Industry. From valuation and performance to stock trends, gainers, and losers.',
  });

  const { isMobile, isTablet, isDesktop } = useUserAgent();
  const shouldDisplayInsightsWidget = marketPageType === MarketPageType.MARKET;

  return (
    <OneColumn>
      <GlobalStyle background={theme.color.charcoal} />
      <DocumentHeader
        title={pageTitle}
        description={metaDescription}
        allowCrawlers={
          !(
            marketPageType === MarketPageType.TERTIARY ||
            latestAnalysis?.companyCount < 5
          )
        }
        titleSuffix={pageTitleSuffix}
      >
        <link rel="canonical" href={`${SWS_HOST}${pathname.toLowerCase()}`} />
      </DocumentHeader>
      {RUNTIME_ENV === 'server' && <MarketFAQ />}
      <Row>
        <Column col={12} colLg={7.5}>
          <Header title={pageTitle} />
          <Media
            defaultMatches={isMobile || isTablet}
            query={{ maxWidth: theme.mdMaxRaw }}
          >
            {(match) =>
              match && (
                <>
                  {shouldDisplayInsightsWidget && (
                    <>
                      {isNewPortfolioSectionEnabled ? (
                        <Box marginTop="x1" marginBottom="x1" paddingLeft="x2">
                          <Heading as="h2" typography="largeStrong">
                            Market Insights
                          </Heading>
                        </Box>
                      ) : null}
                      <RecentArticles />
                    </>
                  )}
                  <CompanyFeeds />
                </>
              )
            }
          </Media>
          <Widget
            anchor={MARKET_VALUATION_ANCHOR}
            key="market-valuation"
            title={marketPerformance}
            description={marketPerformanceSubtitle}
          >
            <MarketPerformance />
            <Summary
              template={
                marketPageType !== MarketPageType.MARKET
                  ? 'industry-valuation'
                  : 'market-valuation'
              }
              title={
                marketPageType !== MarketPageType.MARKET
                  ? 'Current Industry PE'
                  : 'Current Market PE'
              }
            />
            <Summary
              template={
                marketPageType !== MarketPageType.MARKET
                  ? 'industry-past-earnings-growth'
                  : 'past-earnings-growth'
              }
              title="Past Earnings Growth"
            />
          </Widget>
          <Widget
            anchor={MARKET_TRENDS_ANCHOR}
            key="sectors"
            title={sectorTitle}
            description={sectorSubtitle}
            isDeferred
            preRenderHeight={952}
            preRenderHeightMdDown={799}
            preRenderHeightSmDown={894}
          >
            <Sectors />
            <Summary
              template={
                marketPageType !== MarketPageType.MARKET
                  ? 'industry-Sector-PE'
                  : 'market-sector-pe'
              }
              title={
                marketPageType !== MarketPageType.MARKET
                  ? 'Industry PE'
                  : 'Sector PE'
              }
            />
            <Summary
              template={
                marketPageType !== MarketPageType.MARKET
                  ? 'industry-forecasted-growth'
                  : 'market-forecasted-growth'
              }
              title="Forecasted Growth"
            />
          </Widget>
          <Widget
            anchor={MARKET_GAINER_ANCHOR}
            key="market-mover"
            title="Top Stock Gainers and Losers"
            description="Which companies have driven the market over the last 7 days?"
            isDeferred
            preRenderHeight={324}
            preRenderHeightSmDown={264}
          >
            <MarketTable />
          </Widget>
          <ScrollTracking subject="market-page-bottom" />
          <Footer />
        </Column>
        <Media defaultMatches={isDesktop} query={{ minWidth: theme.lgMinRaw }}>
          {(match) =>
            match && (
              <Container colLg={4.5}>
                <FixedSidebar>
                  <CompanyFeeds>
                    {shouldDisplayInsightsWidget ? (
                      <SidebarWrapper>
                        {isNewPortfolioSectionEnabled ? (
                          <Box marginTop="x1" marginBottom="x1">
                            <Heading as="h2" typography="baseStrong">
                              Market Insights
                            </Heading>
                          </Box>
                        ) : null}
                        <RecentArticles />
                      </SidebarWrapper>
                    ) : undefined}
                  </CompanyFeeds>
                </FixedSidebar>
              </Container>
            )
          }
        </Media>
      </Row>
    </OneColumn>
  );
};

export default track({ page: 'MarketPage' })(Market) as typeof Market;
